import { useState, useMemo, useRef, useEffect, ChangeEvent, MouseEvent, ReactNode } from "react";
import {
  Blog,
  BlogStatus,
  fetchAllBlogs,
  deleteBlog,
  updateBlogStatus,
  fetchTrashBlogs,
  restoreBlog,
  purgeBlog
} from "../../services/blogService";
import { useNavigate } from "react-router-dom";
import { Trash2, RotateCcw, Eye, ShieldAlert, CheckCircle } from "lucide-react";

type SortField   = "seoTitle" | "date" | "status" | "createdAt" | "updatedAt";
type SortDir     = "asc" | "desc";
type StatusFilter = "all" | BlogStatus;
interface ToastState   { visible: boolean; message: string; type: "success" | "error"; }
interface ConfirmModal { open: boolean; blogId: number | null; blogTitle: string; mode: "trash" | "purge"; }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function highlight(text: string, query: string): ReactNode {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (<>{text.slice(0, idx)}<mark style={{ background: "rgba(191,61,12,0.3)", color: "var(--or)", borderRadius: 2, padding: "0 1px" }}>{text.slice(idx, idx + query.length)}</mark>{text.slice(idx + query.length)}</>);
}

function StatusBadge({ status }: { status: BlogStatus }) {
  const map = {
    published: { bg: "rgba(109,220,109,0.10)", border: "rgba(109,220,109,0.25)", color: "#6ddc6d", dot: "#6ddc6d", label: "Published" },
    draft:     { bg: "rgba(245,166,35,0.10)",  border: "rgba(245,166,35,0.25)",  color: "#f5a623", dot: "#f5a623", label: "Draft" },
    archived:  { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.12)", color: "#6a6a65", dot: "#6a6a65", label: "Archived" },
  } as const;
  const s = map[status];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, letterSpacing: "0.5px", background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 20, padding: "3px 8px" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />{s.label}
    </span>
  );
}

function IconBtn({ title, onClick, children, danger }: { title: string; onClick: () => void; children: ReactNode; danger?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <button title={title} onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${hov ? (danger ? "rgba(224,82,82,0.35)" : "rgba(191,61,12,0.35)") : "var(--border)"}`, background: hov ? (danger ? "rgba(224,82,82,0.10)" : "var(--or-dim)") : "transparent", color: hov ? (danger ? "#e05252" : "var(--or)") : "var(--text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", flexShrink: 0 }}>
      {children}
    </button>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, marginLeft: 4, opacity: active ? 1 : 0.3 }}>
      <span style={{ fontSize: 8, lineHeight: 1, color: active && dir === "asc"  ? "var(--or)" : "var(--text3)" }}>▲</span>
      <span style={{ fontSize: 8, lineHeight: 1, color: active && dir === "desc" ? "var(--or)" : "var(--text3)" }}>▼</span>
    </span>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ modal, onConfirm, onCancel }: { modal: ConfirmModal; onConfirm: () => void; onCancel: () => void }) {
  if (!modal.open) return null;
  const isPurge = modal.mode === "purge";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onCancel}>
      <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 28, width: 380, maxWidth: "90vw", animation: "modalIn 0.2s ease", boxShadow: "var(--shadow)" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: isPurge ? "rgba(224,82,82,0.12)" : "rgba(245,166,35,0.12)", border: `1px solid ${isPurge ? "rgba(224,82,82,0.25)" : "rgba(245,166,35,0.25)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 16 }}>
          {isPurge ? <ShieldAlert size={20} color="#e05252" /> : <Trash2 size={20} color="#f5a623" />}
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>
          {isPurge ? "Permanently Delete Post?" : "Move Blog Post to Trash?"}
        </div>
        <div style={{ fontSize: 14, color: "var(--text3)", lineHeight: 1.6, marginBottom: 24 }}>
          <strong style={{ color: "var(--text2)" }}>{modal.blogTitle}</strong> {isPurge ? "will be permanently deleted from the database. This action cannot be undone." : "will be moved to the Trash Bin. You can restore it at any time."}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${isPurge ? "rgba(224,82,82,0.35)" : "rgba(245,166,35,0.35)"}`, background: isPurge ? "rgba(224,82,82,0.12)" : "rgba(245,166,35,0.12)", color: isPurge ? "#e05252" : "#f5a623", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            {isPurge ? "Permanently Delete" : "Move to Trash"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── View Modal ───────────────────────────────────────────────────────────────

function ViewModal({ blog, onClose }: { blog: Blog | null; onClose: () => void }) {
  if (!blog) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 18, width: 640, maxWidth: "95vw", maxHeight: "88vh", overflow: "hidden", display: "flex", flexDirection: "column", animation: "modalIn 0.2s ease", boxShadow: "var(--shadow)" }}>

        {/* Cover strip */}
        <div style={{
          height: 120,
          backgroundImage: blog.coverImageUrl ? `url(${blog.coverImageUrl})` : (blog.coverGradient || "none"),
          backgroundColor: "var(--card)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          flexShrink: 0,
          position: "relative",
          display: "flex",
          alignItems: "flex-end",
          padding: "12px 20px"
        }}>
          <button onClick={onClose} style={{ position: "absolute", top: 12, right: 12, width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(0,0,0,0.4)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", padding: "24px 26px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Status + date row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <StatusBadge status={blog.status} />
            {blog.readTime && <span style={{ fontSize: 12, color: "var(--text3)" }}>{blog.readTime}</span>}
            {blog.date && <span style={{ fontSize: 12, color: "var(--text3)" }}>· {blog.date}</span>}
          </div>

          {/* Title */}
          <h2 style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 600, color: "var(--text)", lineHeight: 1.3, letterSpacing: "-0.4px" }}>{blog.seoTitle || "Untitled"}</h2>
          {blog.subtitle && <p style={{ margin: 0, fontSize: 14, color: "var(--text3)", lineHeight: 1.6 }}>{blog.subtitle}</p>}

          {/* Body content */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>
              HTML Body Content Preview
            </div>
            <div
              className="rich-editor-canvas"
              style={{
                background: "rgba(255, 255, 255, 0.01)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: "14px 18px",
                maxHeight: 250,
                overflowY: "auto",
                fontSize: 13,
                color: "var(--text2)"
              }}
            >
              {typeof blog.body === "string" ? (
                <div dangerouslySetInnerHTML={{ __html: blog.body }} />
              ) : (
                blog.body?.map((sec: any, idx: number) => {
                  if (sec.type === "heading") return <h3 key={idx}>{sec.text}</h3>;
                  return <p key={idx}>{sec.text}</p>;
                })
              )}
            </div>
          </div>

          {/* Meta grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {([["Slug", blog.slug], ["SEO Title", blog.seoTitle || "–"], ["Created", blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : "–"], ["Updated", blog.updatedAt ? new Date(blog.updatedAt).toLocaleDateString() : "–"]] as [string, string][]).map(([label, value]) => (
              <div key={label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 12, color: "var(--text2)", wordBreak: "break-all" }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AllBlogs() {
  const [blogs, setBlogs]         = useState<Blog[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatusFilter]     = useState<StatusFilter>("all");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDir, setSortDir]     = useState<SortDir>("desc");
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(10);
  const [selected, setSelected]   = useState<Set<number>>(new Set());
  const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ open: false, blogId: null, blogTitle: "", mode: "trash" });
  const [viewBlog, setViewBlog]   = useState<Blog | null>(null);
  const [toast, setToast]         = useState<ToastState>({ visible: false, message: "", type: "success" });
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  // Active Tab: "active" (normal blogs) or "trash" (soft deleted blogs)
  const [activeTab, setActiveTab] = useState<"active" | "trash">("active");

  const loadBlogs = () => {
    setLoading(true);
    const fetchCall = activeTab === "active" ? fetchAllBlogs : fetchTrashBlogs;
    fetchCall()
      .then((data) => {
        setBlogs(data);
        setSelected(new Set());
      })
      .catch(() => {
        showToast("Failed to load blogs", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadBlogs();
  }, [activeTab]);

  const showToast = (message: string, type: ToastState["type"] = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ visible: true, message, type });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  };

  const filtered = useMemo(() => {
    let list = [...blogs];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) => b.seoTitle?.toLowerCase().includes(q) || b.slug?.toLowerCase().includes(q) || String(b.id).includes(q));
    }
    if (statusFilter !== "all") list = list.filter((b) => b.status === statusFilter);
    list.sort((a, b) => {
      const cmp = String(a[sortField] ?? "").localeCompare(String(b[sortField] ?? ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [blogs, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageSlice  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  const allSelected = pageSlice.length > 0 && pageSlice.every((b) => selected.has(b.id));
  const toggleSelectAll = () => { const next = new Set(selected); if (allSelected) pageSlice.forEach((b) => next.delete(b.id)); else pageSlice.forEach((b) => next.add(b.id)); setSelected(next); };
  const toggleSelect = (id: number) => { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next); };
  const toggleSort = (field: SortField) => { if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortField(field); setSortDir("asc"); } };

  const handleDeleteConfirm = async () => {
    if (!confirmModal.blogId) return;
    try {
      if (confirmModal.mode === "trash") {
        await deleteBlog(confirmModal.blogId);
        showToast("Blog moved to Trash");
      } else {
        await purgeBlog(confirmModal.blogId);
        showToast("Blog permanently deleted");
      }
      setBlogs((prev) => prev.filter((b) => b.id !== confirmModal.blogId));
      setSelected((prev) => { const n = new Set(prev); n.delete(confirmModal.blogId!); return n; });
    } catch {
      showToast("Operation failed.", "error");
    } finally {
      setConfirmModal({ open: false, blogId: null, blogTitle: "", mode: "trash" });
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await restoreBlog(id);
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      showToast("Blog post restored!");
    } catch {
      showToast("Restore failed.", "error");
    }
  };

  const handleStatusChange = async (id: number, status: BlogStatus) => {
    try {
      await updateBlogStatus(id, status);
      setBlogs((prev) => prev.map((b) => b.id === id ? { ...b, status } : b));
      showToast(`Status updated to "${status}"`);
    } catch { showToast("Status update failed.", "error"); }
  };

  const stats = useMemo(() => ({
    total: blogs.length,
    published: blogs.filter((b) => b.status === "published").length,
    draft: blogs.filter((b) => b.status === "draft").length,
    archived: blogs.filter((b) => b.status === "archived").length,
  }), [blogs]);

  const pageNumbers = (): (number | "…")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage <= 4) return [1,2,3,4,5,"…",totalPages];
    if (safePage >= totalPages - 3) return [1,"…",totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages];
    return [1,"…",safePage-1,safePage,safePage+1,"…",totalPages];
  };

  const selectStyle: React.CSSProperties = {
    fontSize: 13, padding: "7px 28px 7px 12px", borderRadius: 8,
    border: "1px solid var(--border)", background: "var(--card-solid)",
    color: "var(--text2)", cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif", outline: "none", appearance: "none",
  };

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes modalIn     { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        .bl-row { transition: background 0.12s; }
        .bl-row:hover { background: rgba(255,255,255,0.025) !important; }
        .bl-th { user-select: none; cursor: pointer; }
        .bl-th:hover { color: var(--text) !important; }
        .bl-filter-btn { transition: all 0.15s; }
        .bl-filter-btn:hover { background: rgba(255,255,255,0.05) !important; }
        select option { background: var(--card-solid) !important; color: var(--text) !important; }
        
        .rte-tab-btn {
          padding: 10px 18px;
          border: none;
          background: transparent;
          color: var(--text3);
          font-weight: 500;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          border-bottom: 2px solid transparent;
          transition: all 0.15s;
        }
        .rte-tab-btn.active {
          color: var(--text);
          border-bottom-color: var(--or);
        }
        
        /* HTML body styles inside ViewModal */
        .rich-editor-canvas table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 13px;
        }
        .rich-editor-canvas table th, 
        .rich-editor-canvas table td {
          border: 1px solid var(--border);
          padding: 8px 12px;
          text-align: left;
        }
        .rich-editor-canvas table th {
          background: rgba(255, 255, 255, 0.04);
          font-weight: 600;
          color: var(--text);
        }
        .rich-editor-canvas table td {
          color: var(--text2);
        }
        .rich-editor-canvas ul {
          list-style: disc;
          margin: 0 0 16px;
          padding-left: 20px;
          color: var(--text2);
        }
        .rich-editor-canvas ol {
          list-style: decimal;
          margin: 0 0 16px;
          padding-left: 20px;
          color: var(--text2);
        }
        .rich-editor-canvas li {
          margin-bottom: 6px;
          line-height: 1.5;
        }
        .rich-editor-canvas blockquote {
          background: rgba(191, 61, 12, 0.08);
          border: 1px solid rgba(191, 61, 12, 0.2);
          border-left: 3px solid var(--or);
          border-radius: 0 8px 8px 0;
          padding: 12px 16px;
          margin: 16px 0;
        }
        .rich-editor-canvas blockquote p {
          margin: 0;
          color: var(--text2);
          font-style: italic;
        }
        
        @media (max-width: 768px) { .bl-stat { grid-template-columns: repeat(2,1fr) !important; } }
      `}</style>

      {/* Toast */}
      {toast.visible && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 600, background: toast.type === "success" ? "#1a2e1a" : "#2e1a1a", border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : "rgba(224,82,82,0.25)"}`, borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
          <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : "#e08b8b" }}>{toast.type === "success" ? "✓" : "✕"}&nbsp;&nbsp;{toast.message}</span>
        </div>
      )}

      <DeleteModal modal={confirmModal} onConfirm={handleDeleteConfirm} onCancel={() => setConfirmModal({ open: false, blogId: null, blogTitle: "", mode: "trash" })} />
      <ViewModal blog={viewBlog} onClose={() => setViewBlog(null)} />

      <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

          {/* ── Header ── */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>Blogs</div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>
                {activeTab === "active" ? "Blog Management" : "Trash Bin"}
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>{loading ? "Loading…" : `${blogs.length} total posts in this view`}</p>
            </div>
            <button onClick={() => navigate("/manage/blog")} style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 9, border: "1px solid rgba(191,61,12,0.35)", background: "linear-gradient(135deg, #BF3D0C, #E8593C)", color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", boxShadow: "0 2px 12px rgba(191,61,12,0.25)", marginTop: 6 }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Blog Post
            </button>
          </div>

          {/* ── Tabs selector ── */}
          <div style={{ display: "flex", gap: 8, borderBottom: "1px solid var(--border)", marginBottom: 20 }}>
            <button type="button" className={`rte-tab-btn ${activeTab === "active" ? "active" : ""}`} onClick={() => setActiveTab("active")}>Active Blogs</button>
            <button type="button" className={`rte-tab-btn ${activeTab === "trash" ? "active" : ""}`} onClick={() => setActiveTab("trash")}>Trash Bin</button>
          </div>

          {/* ── Stat cards ── */}
          {activeTab === "active" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }} className="bl-stat">
              {[
                { label: "Total Active", value: stats.total,     color: "var(--text2)", dot: "var(--text3)" },
                { label: "Published",    value: stats.published, color: "#6ddc6d",       dot: "#6ddc6d" },
                { label: "Draft",        value: stats.draft,     color: "#f5a623",       dot: "#f5a623" },
                { label: "Archived",     value: stats.archived,  color: "var(--text3)", dot: "var(--text3)" },
              ].map((s) => (
                <div key={s.label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 600, color: s.color, lineHeight: 1 }}>{loading ? "–" : s.value}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, opacity: 0.6 }} />
                </div>
              ))}
            </div>
          )}

          {/* ── Table card ── */}
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>

            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
              {/* Search */}
              <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text3)", fontSize: 14, pointerEvents: "none" }}>⌕</span>
                <input value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder="Search by title or slug…" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px 8px 32px", fontSize: 13, color: "var(--text)", outline: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" as const }} />
                {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14 }}>×</button>}
              </div>

              {/* Status filter pills (Active only) */}
              {activeTab === "active" && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {(["all", "published", "draft", "archived"] as StatusFilter[]).map((s) => (
                    <button key={s} className="bl-filter-btn" onClick={() => setStatusFilter(s)} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 7, border: `1px solid ${statusFilter === s ? "rgba(191,61,12,0.3)" : "var(--border)"}`, background: statusFilter === s ? "var(--or-dim)" : "transparent", color: statusFilter === s ? "var(--or)" : "var(--text3)", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textTransform: "capitalize" }}>
                      {s === "all" ? "All status" : s}
                    </button>
                  ))}
                </div>
              )}

              {/* Bulk operations */}
              {selected.size > 0 && (
                <button onClick={async () => {
                  try {
                    if (activeTab === "active") {
                      await Promise.all(Array.from(selected).map((id) => deleteBlog(id)));
                      showToast(`${selected.size} post(s) moved to Trash`);
                    } else {
                      await Promise.all(Array.from(selected).map((id) => purgeBlog(id)));
                      showToast(`${selected.size} post(s) permanently deleted`);
                    }
                    setBlogs((prev) => prev.filter((b) => !selected.has(b.id)));
                    setSelected(new Set());
                  } catch { showToast("Bulk action failed.", "error"); }
                }} style={{ fontSize: 13, padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(224,82,82,0.3)", background: "rgba(224,82,82,0.08)", color: "#e05252", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  {activeTab === "active" ? "Move Selected to Trash" : "Delete Permanently"} ({selected.size})
                </button>
              )}
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px 16px", width: 40, textAlign: "left" }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ cursor: "pointer", accentColor: "var(--or)" }} />
                    </th>
                    {([["seoTitle","Title"],["createdAt","Created"],["updatedAt","Updated"]] as [SortField,string][]).map(([field, label]) => (
                      <th key={field} className="bl-th" onClick={() => toggleSort(field)} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", whiteSpace: "nowrap" }}>
                        {label}<SortIcon active={sortField === field} dir={sortDir} />
                      </th>
                    ))}
                    {activeTab === "active" && (
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Status</th>
                    )}
                    <th style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} style={{ padding: "56px 24px", textAlign: "center", color: "var(--text3)", fontSize: 14 }}>Loading blog posts…</td></tr>
                  ) : pageSlice.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: "56px 24px", textAlign: "center" }}>
                      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>✍️</div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>{search ? `No posts match "${search}"` : (activeTab === "active" ? "No active blog posts found" : "Trash Bin is empty")}</div>
                      <div style={{ fontSize: 13, color: "var(--text3)" }}>{search ? "Try a different search term." : (activeTab === "active" ? "Create your first blog post to get started." : "Blogs you delete will appear here.")}</div>
                    </td></tr>
                  ) : pageSlice.map((blog) => (
                    <tr key={blog.id} className="bl-row" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: selected.has(blog.id) ? "rgba(191,61,12,0.04)" : "transparent" }}>
                      <td style={{ padding: "14px 16px" }}>
                        <input type="checkbox" checked={selected.has(blog.id)} onChange={() => toggleSelect(blog.id)} style={{ cursor: "pointer", accentColor: "var(--or)" }} />
                      </td>
                      <td style={{ padding: "14px 16px", maxWidth: 260 }}>
                        <div style={{ fontWeight: 500, color: "var(--text)", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{highlight(blog.seoTitle || "Untitled", search)}</div>
                        <div style={{ fontSize: 11, color: "var(--text3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{blog.slug}</div>
                      </td>
                      <td style={{ padding: "14px 16px", color: "var(--text3)", whiteSpace: "nowrap" }}>{blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : "–"}</td>
                      <td style={{ padding: "14px 16px", color: "var(--text3)", whiteSpace: "nowrap" }}>{blog.updatedAt ? new Date(blog.updatedAt).toLocaleDateString() : "–"}</td>
                      {activeTab === "active" && (
                        <td style={{ padding: "14px 16px" }}><StatusBadge status={blog.status} /></td>
                      )}
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
                          {/* View */}
                          <IconBtn title="View Preview" onClick={() => setViewBlog(blog)}>
                            <Eye size={13} />
                          </IconBtn>

                          {activeTab === "active" ? (
                            <>
                              {/* Publish quick toggle */}
                              {blog.status !== "published" && (
                                <IconBtn title="Publish" onClick={() => handleStatusChange(blog.id, "published")}>
                                  <CheckCircle size={13} />
                                </IconBtn>
                              )}
                              {/* Draft quick toggle */}
                              {blog.status !== "draft" && (
                                <IconBtn title="Set to Draft" onClick={() => handleStatusChange(blog.id, "draft")}>
                                  <RotateCcw size={13} />
                                </IconBtn>
                              )}
                              {/* Edit */}
                              <IconBtn title="Edit" onClick={() => navigate(`/manage/blog?id=${blog.id}`)}>
                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M11 2l3 3-9 9H2v-3l9-9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                              </IconBtn>
                              {/* Delete */}
                              <IconBtn title="Move to Trash" danger onClick={() => setConfirmModal({ open: true, blogId: blog.id, blogTitle: blog.seoTitle || "Untitled", mode: "trash" })}>
                                <Trash2 size={13} />
                              </IconBtn>
                            </>
                          ) : (
                            <>
                              {/* Restore */}
                              <IconBtn title="Restore Blog" onClick={() => handleRestore(blog.id)}>
                                <RotateCcw size={13} />
                              </IconBtn>
                              {/* Permanent Delete */}
                              <IconBtn title="Delete Permanently" danger onClick={() => setConfirmModal({ open: true, blogId: blog.id, blogTitle: blog.seoTitle || "Untitled", mode: "purge" })}>
                                <Trash2 size={13} />
                              </IconBtn>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 13, color: "var(--text3)" }}>
                {filtered.length === 0 ? "No results" : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length}`}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {pageNumbers().map((n, i) => n === "…" ? (
                  <span key={`e${i}`} style={{ padding: "0 4px", color: "var(--text3)", fontSize: 13 }}>…</span>
                ) : (
                  <button key={n} onClick={() => setPage(n as number)} style={{ width: 32, height: 32, borderRadius: 7, border: `1px solid ${safePage === n ? "rgba(191,61,12,0.3)" : "var(--border)"}`, background: safePage === n ? "var(--or-dim)" : "transparent", color: safePage === n ? "var(--or)" : "var(--text3)", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>{n}</button>
                ))}
              </div>
              <select value={pageSize} onChange={(e: ChangeEvent<HTMLSelectElement>) => setPageSize(Number(e.target.value))} style={{ ...selectStyle, padding: "6px 10px" }}>
                {[10, 20, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
